/**
 * Kelas feature — entry point
 *
 * Structure:
 *   hooks/useKelas.js             ← list, toggle, delete, save
 *   components/KelasCard.jsx      ← single card in grid
 *   components/KelasModal.jsx     ← add/edit form modal
 *   components/KelasPreviewModal.jsx ← full-size preview
 *   components/StarRating.jsx     ← interactive gold stars
 *   constants.js                  ← CATEGORIES_SUGGEST, CATEGORY_COLORS, parseKat, GOLD
 */
import { useState } from 'react'
import { RiAddLine, RiSearchLine } from 'react-icons/ri'
import { Btn, Select, PageHeader, EmptyState, Pagination, FilterBar } from '../../components/ui/design.jsx'
import { useKelas } from './hooks/useKelas.js'
import { CATEGORIES_SUGGEST } from './constants.js'
import KelasCard from './components/KelasCard.jsx'
import KelasModal from './components/KelasModal.jsx'
import KelasPreviewModal from './components/KelasPreviewModal.jsx'

export default function KelasPage() {
  const { items, loading, pagination, load, toggleActive, remove, save } = useKelas()
  const [search, setSearch] = useState('')
  const [filterKategori, setFilterKategori] = useState('')
  const [filterActive, setFilterActive] = useState('')
  const [previewing, setPreviewing] = useState(null)
  const [editing, setEditing] = useState(null)

  const handleSearch = (e) => {
    if (e.key === 'Enter') load(1, { search, filterKategori, filterActive })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kelas / Program"
        description="Program pelatihan yang tampil di bagian Jadwal website."
        action={
          <Btn onClick={() => setEditing('new')}>
            <RiAddLine /> Tambah Kelas
          </Btn>
        }
      />

      <FilterBar>
        <Select value={filterKategori} onChange={(e) => { setFilterKategori(e.target.value); load(1) }} className="w-44">
          <option value="">Semua kategori</option>
          {CATEGORIES_SUGGEST.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
        <div className="relative">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" />
          <input
            placeholder="Cari nama kelas"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearch}
            className="h-9 w-52 rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
        <Select value={filterActive} onChange={(e) => { setFilterActive(e.target.value); load(1) }} className="w-36">
          <option value="">Semua status</option>
          <option value="true">Aktif saja</option>
          <option value="false">Nonaktif saja</option>
        </Select>
      </FilterBar>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {loading && <EmptyState message="Memuat…" />}
        {!loading && items.length === 0 && <EmptyState message="Belum ada kelas." />}
        {items.map((item) => (
          <KelasCard
            key={item.id}
            item={item}
            onPreview={setPreviewing}
            onToggle={toggleActive}
            onEdit={setEditing}
            onRemove={remove}
          />
        ))}
      </div>

      <Pagination pagination={pagination} onPage={(p) => load(p, { search, filterKategori, filterActive })} />

      <KelasPreviewModal item={previewing} onClose={() => setPreviewing(null)} />

      {editing && (
        <KelasModal
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={async (form) => { await save(form); setEditing(null) }}
        />
      )}
    </div>
  )
}
